/**
 * Generador de PDF para propuestas comerciales.
 * v3: soporta variantes FULL y EXPRESS + retorna el Blob del PDF para poder
 * subirlo al backend y compartirlo por WhatsApp.
 *
 * @param {HTMLElement} rootEl - contenedor con .pdf-page hijos
 * @param {object} options - { filename?: string, download?: boolean }
 * @returns {Promise<Blob>} - el PDF generado como Blob
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PDF_WIDTH_MM = 210;
const PDF_HEIGHT_MM = 297;

export async function generateProposalPDF(rootEl, options = {}) {
  const { filename, download = true } = typeof options === 'string' ? { filename: options } : options;
  if (!rootEl) throw new Error('generateProposalPDF: rootEl requerido');
  const pages = rootEl.querySelectorAll('.pdf-page');
  if (!pages.length) throw new Error('No se encontraron páginas para exportar');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    await waitForImages(page);
    const canvas = await html2canvas(page, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#0a0a0a',
      logging: false,
      allowTaint: true,
    });
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    if (i > 0) pdf.addPage();

    const ratio = canvas.width / canvas.height;
    let w = PDF_WIDTH_MM;
    let h = w / ratio;
    if (h > PDF_HEIGHT_MM) {
      h = PDF_HEIGHT_MM;
      w = h * ratio;
    }
    const xMm = (PDF_WIDTH_MM - w) / 2;
    const yMm = (PDF_HEIGHT_MM - h) / 2;
    pdf.addImage(imgData, 'JPEG', xMm, yMm, w, h);

    // Add clickable link annotations
    const pageRect = page.getBoundingClientRect();
    const scaleX = w / pageRect.width;
    const scaleY = h / pageRect.height;
    const linkEls = page.querySelectorAll('[data-pdf-link]');
    linkEls.forEach((el) => {
      const url = el.getAttribute('data-pdf-link');
      if (!url) return;
      const r = el.getBoundingClientRect();
      const linkX = xMm + (r.left - pageRect.left) * scaleX;
      const linkY = yMm + (r.top - pageRect.top) * scaleY;
      const linkW = r.width * scaleX;
      const linkH = r.height * scaleY;
      pdf.link(linkX, linkY, linkW, linkH, { url });
    });
  }

  if (download && filename) pdf.save(filename);
  return pdf.output('blob');
}

function waitForImages(el) {
  const imgs = Array.from(el.querySelectorAll('img'));
  // Also preload CSS background-images (used for talent portraits to avoid
  // aspect-ratio distortion — html2canvas respects `background-size: cover`
  // reliably but only if the image is already cached in the browser).
  const bgUrls = [];
  el.querySelectorAll('*').forEach((node) => {
    const bg = window.getComputedStyle(node).backgroundImage;
    if (bg && bg !== 'none') {
      const m = bg.match(/url\(["']?([^"')]+)["']?\)/);
      if (m && m[1]) bgUrls.push(m[1]);
    }
  });
  const promises = imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
      setTimeout(resolve, 5000);
    });
  });
  const bgPromises = bgUrls.map((url) => new Promise((resolve) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve();
    im.onerror = () => resolve();
    im.src = url;
    setTimeout(resolve, 5000);
  }));
  return Promise.all([...promises, ...bgPromises]);
}
